import { Capacitor } from '@capacitor/core';
import { CapacitorPasskey } from '@capgo/capacitor-passkey';
import type { CreateCredentialOptions, GetCredentialOptions } from '@capgo/capacitor-passkey';
import apiService, { setAccessToken, handleRefreshTokenFromResponse, clearRefreshFailure } from './api';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import type { User } from '../types';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

export interface EnrolledMethods {
  totp: boolean;
  email: boolean;
  passkeys: Array<{
    id: number;
    nickname: string | null;
    created_at: string;
    last_used_at: string | null;
  }>;
}

export interface AvailableMethods {
  totp: boolean;
  email: boolean;
  passkey: boolean;
}

export interface TotpSetup {
  otpauthUri: string;
  qrDataUrl: string;
  manualEntrySecret: string;
}

export interface TrustedDevice {
  id: number;
  device_label: string | null;
  ip_address: string | null;
  created_at: string;
  last_seen_at: string | null;
  expires_at: string;
}

/** What the server hands back after a change to sign-in methods. */
interface RenewedSession {
  accessToken?: string;
  expiresAt?: string;
  refreshToken?: string;
}

/**
 * Why passkeys cannot be used here, or null when they can.
 *
 * The app runs in a web view, which has passkeys switched off unless the shell
 * asks for them. The Android shell does from the release after 1.2.86
 * (android/.../PasskeySupportPlugin.java) and says so with a marker on the
 * user agent. The marker is the test, not the platform: this bundle reaches
 * older shells by live update, and there the buttons would only fail.
 *
 * On iOS the web view cannot do it at all: the app's page is served from
 * capacitor://localhost, and WebAuthn refuses a passkey for a domain the page
 * is not on. Passkeys there go through the native plugin (`usesNativePasskeys`),
 * and the test is whether the shell has it.
 */
const ANDROID_SHELL_PASSKEYS = /\bFactreePasskeys\//;
const NATIVE_PASSKEY_PLUGIN = 'CapacitorPasskey';
const UPDATE_THE_APP = 'Passkeys need a newer version of the app. Update it, or sign in here with an emailed code or your authenticator app.';

/** iOS app: passkeys are made and used by the system, through the plugin. */
function usesNativePasskeys(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

export function passkeyUnavailableReason(): string | null {
  if (usesNativePasskeys()) {
    return Capacitor.isPluginAvailable(NATIVE_PASSKEY_PLUGIN) ? null : UPDATE_THE_APP;
  }
  if (Capacitor.isNativePlatform() && !ANDROID_SHELL_PASSKEYS.test(navigator.userAgent)) {
    return UPDATE_THE_APP;
  }
  if (!browserSupportsWebAuthn()) {
    return 'This browser does not support passkeys.';
  }
  return null;
}

/**
 * A passkey prompt fails for reasons the browser reports only as error names.
 * The one worth spelling out: the device already holds a passkey for this
 * account, which is what a password manager synced to it means, and the
 * browser then refuses to make a second.
 */
export function passkeyErrorMessage(err: unknown, fallback: string): string {
  const e = err as { name?: string; message?: string } | null;
  if (isPasskeyAlreadyHere(err)) {
    return 'This device already has a passkey for your account, most likely from a password manager that syncs to it. There is nothing to add: choose "Use passkey" when you sign in.';
  }
  if (e?.name === 'NotAllowedError') {
    return 'The passkey prompt was cancelled or timed out.';
  }
  return e?.message || fallback;
}

export function isPasskeyAlreadyHere(err: unknown): boolean {
  const e = err as { name?: string; code?: string } | null;
  return e?.name === 'InvalidStateError' || e?.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED';
}

/**
 * The plugin rejects with the WebAuthn error name in `code`; the browser puts
 * it in `name`. The pages read `name`.
 */
function asWebAuthnError(err: unknown): unknown {
  const e = err as { name?: string; code?: string; message?: string } | null;
  if (!e?.code) return err;
  const named = new Error(e.message || e.code);
  named.name = e.code;
  return named;
}

/**
 * Make or use a passkey. In a browser, and in the Android app, that is the
 * page's own WebAuthn call. In the iOS app it is the plugin, which takes and
 * returns the same JSON the server speaks. The origin is the site the passkey
 * belongs to, read from the server's options: the app is linked to it by
 * /.well-known/apple-app-site-association and its associated-domains
 * entitlement, both written by the workflows.
 */
async function createPasskey(options: PublicKeyCredentialCreationOptionsJSON): Promise<unknown> {
  if (!usesNativePasskeys()) return startRegistration({ optionsJSON: options });
  try {
    return await CapacitorPasskey.createCredential({
      origin: options.rp.id ? `https://${options.rp.id}` : undefined,
      publicKey: options as unknown as CreateCredentialOptions['publicKey'],
    });
  } catch (err) {
    throw asWebAuthnError(err);
  }
}

async function signInWithPasskey(options: PublicKeyCredentialRequestOptionsJSON): Promise<unknown> {
  if (!usesNativePasskeys()) return startAuthentication({ optionsJSON: options });
  try {
    return await CapacitorPasskey.getCredential({
      origin: options.rpId ? `https://${options.rpId}` : undefined,
      publicKey: options as unknown as GetCredentialOptions['publicKey'],
    });
  } catch (err) {
    throw asWebAuthnError(err);
  }
}

class MfaService {
  /**
   * Changing a method ends every session on the account. The server gives
   * this one a new session in the same response; take it, or the next request
   * is refused and the page that made the change is signed out. Returns false
   * for a server that sends none.
   */
  private keepSession(data: unknown): boolean {
    const session = data as RenewedSession | null | undefined;
    if (!session?.accessToken) return false;
    setAccessToken(session.accessToken, session.expiresAt);
    handleRefreshTokenFromResponse(session);
    clearRefreshFailure();
    return true;
  }

  // -----------------------------------------------------------------------
  // Enrolled methods
  // -----------------------------------------------------------------------
  async getMethods(): Promise<EnrolledMethods> {
    const res = await apiService.get<EnrolledMethods>('/auth/mfa/methods');
    if (!res.success || !res.data) throw new Error('Failed to load MFA methods');
    return res.data;
  }

  // -----------------------------------------------------------------------
  // TOTP
  // -----------------------------------------------------------------------
  async setupTotp(): Promise<TotpSetup> {
    const res = await apiService.post<TotpSetup>('/auth/mfa/totp/setup');
    if (!res.success || !res.data) throw new Error('TOTP setup failed');
    return res.data;
  }

  async confirmTotp(code: string): Promise<void> {
    const res = await apiService.post('/auth/mfa/totp/confirm', { code });
    if (!res.success) throw new Error(res.message || 'Invalid code');
    this.keepSession(res.data);
  }

  async disableTotp(password: string): Promise<void> {
    const res = await apiService.post('/auth/mfa/totp/disable', { password });
    if (!res.success) throw new Error(res.message || 'Disable failed');
    this.keepSession(res.data);
  }

  // -----------------------------------------------------------------------
  // Email OTP
  // -----------------------------------------------------------------------
  async enableEmailOtp(): Promise<void> {
    const res = await apiService.post('/auth/mfa/email-otp/enable');
    if (!res.success) throw new Error(res.message || 'Failed to send code');
  }

  async confirmEmailOtp(code: string): Promise<void> {
    const res = await apiService.post('/auth/mfa/email-otp/confirm', { code });
    if (!res.success) throw new Error(res.message || 'Invalid code');
    this.keepSession(res.data);
  }

  async disableEmailOtp(): Promise<void> {
    const res = await apiService.post('/auth/mfa/email-otp/disable');
    if (!res.success) throw new Error(res.message || 'Disable failed');
    this.keepSession(res.data);
  }

  // -----------------------------------------------------------------------
  // Passkeys / WebAuthn
  // -----------------------------------------------------------------------
  /** Resolves to whether this device is still signed in afterwards. */
  async registerPasskey(nickname: string): Promise<boolean> {
    const optsRes = await apiService.post<PublicKeyCredentialCreationOptionsJSON>(
      '/auth/mfa/passkey/register-options'
    );
    if (!optsRes.success || !optsRes.data) throw new Error('Failed to start registration');

    const attestationResponse = await createPasskey(optsRes.data);

    const verifyRes = await apiService.post('/auth/mfa/passkey/register-verify', {
      attestationResponse,
      nickname,
    });
    if (!verifyRes.success) throw new Error(verifyRes.message || 'Passkey registration failed');
    return this.keepSession(verifyRes.data);
  }

  async deletePasskey(id: number): Promise<void> {
    const res = await apiService.delete(`/auth/mfa/passkey/${id}`);
    if (!res.success) throw new Error('Failed to delete passkey');
    this.keepSession(res.data);
  }

  // -----------------------------------------------------------------------
  // Login step-up (unauthenticated — uses mfaTicket)
  // -----------------------------------------------------------------------
  async sendEmailChallenge(mfaTicket: string): Promise<void> {
    const res = await apiService.post('/auth/mfa/challenge', { mfaTicket, method: 'email' });
    if (!res.success) throw new Error(res.message || 'Failed to send code');
  }

  async getPasskeyChallenge(mfaTicket: string): Promise<PublicKeyCredentialRequestOptionsJSON> {
    const res = await apiService.post<PublicKeyCredentialRequestOptionsJSON>('/auth/mfa/challenge', {
      mfaTicket,
      method: 'passkey',
    });
    if (!res.success || !res.data) throw new Error('Failed to start passkey challenge');
    return res.data;
  }

  async verifyTotp(
    mfaTicket: string,
    code: string,
    options: { trustDevice?: boolean; rememberMe?: boolean } = {}
  ) {
    return this.verify({ mfaTicket, method: 'totp', code, ...options });
  }

  async verifyEmail(
    mfaTicket: string,
    code: string,
    options: { trustDevice?: boolean; rememberMe?: boolean } = {}
  ) {
    return this.verify({ mfaTicket, method: 'email', code, ...options });
  }

  async verifyPasskey(
    mfaTicket: string,
    options: { trustDevice?: boolean; rememberMe?: boolean } = {}
  ) {
    const challenge = await this.getPasskeyChallenge(mfaTicket);
    const assertionResponse = await signInWithPasskey(challenge);
    return this.verify({ mfaTicket, method: 'passkey', assertionResponse, ...options });
  }

  private async verify(payload: {
    mfaTicket: string;
    method: 'totp' | 'email' | 'passkey';
    code?: string;
    assertionResponse?: unknown;
    trustDevice?: boolean;
    rememberMe?: boolean;
  }) {
    const res = await apiService.post<{
      user: User;
      accessToken: string;
      expiresAt?: string;
      refreshToken?: string;
    }>('/auth/mfa/verify', payload);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Verification failed');
    }
    return res.data;
  }

  // -----------------------------------------------------------------------
  // Trusted devices
  // -----------------------------------------------------------------------
  async listTrustedDevices(): Promise<TrustedDevice[]> {
    const res = await apiService.get<TrustedDevice[]>('/auth/mfa/trusted-devices');
    return res.success ? (res.data || []) : [];
  }

  async revokeTrustedDevice(id: number): Promise<void> {
    await apiService.delete(`/auth/mfa/trusted-devices/${id}`);
  }

  // -----------------------------------------------------------------------
  // Org policy (admin)
  // -----------------------------------------------------------------------
  async getOrgPolicy(): Promise<{
    mfa_required: boolean;
    mfa_required_since: string | null;
    mfa_grace_period_days: number;
  } | null> {
    const res = await apiService.get('/auth/mfa/org-policy');
    return res.success ? (res.data as { mfa_required: boolean; mfa_required_since: string | null; mfa_grace_period_days: number } | null) : null;
  }

  async setOrgPolicy(required: boolean, gracePeriodDays: number = 14): Promise<void> {
    const res = await apiService.put('/auth/mfa/org-policy', { required, gracePeriodDays });
    if (!res.success) throw new Error('Failed to update policy');
  }
}

const mfaService = new MfaService();
export default mfaService;
